/**
 * BomPanel — Bill of Materials (engineering breakdown).
 *
 * BoM is OUR engineering tree: for each BoQ line (what the buyer wants),
 * which catalog SKUs / sub-assemblies do we need to source, and in what
 * qty. Distinct from BoQ (buyer's wishlist) and Bid Matrix (scoring).
 *
 * Demo-week scope: placeholder explaining the concept + showing the
 * count of BoQ lines that need a BoM expansion. Full tree editor (parent
 * → child SKUs with quantities, sourced from BomNode model) ships
 * post-demo.
 */
import React, { useEffect, useState } from "react";
import { v2 } from "@/lib/v2Api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GitBranch, Wrench, Info } from "lucide-react";

function BomPanel({ rfpId }) {
  const [boqCount, setBoqCount] = useState(null);

  useEffect(() => {
    if (!rfpId) return;
    v2.get(`/rfp/${rfpId}`)
      .then((r) => setBoqCount(r.data?.analysis_summary?.boq_count ?? 0))
      .catch(() => setBoqCount(null));
  }, [rfpId]);

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-purple-600" />
            Bill of Materials
            {boqCount != null && (
              <Badge variant="outline">{boqCount} BoQ lines pending expansion</Badge>
            )}
          </CardTitle>
          <CardDescription>
            The engineering breakdown of what we'll deliver — for each BoQ line, which catalog
            SKUs and sub-assemblies are needed, and in what quantity.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border border-dashed border-slate-200 rounded-lg p-6 bg-slate-50">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-2 text-sm text-slate-700">
                <p className="font-medium">BoM editor coming soon.</p>
                <p>
                  This tab will let you build the engineering tree per bid:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                  <li>Auto-suggest catalog SKUs that match each BoQ line</li>
                  <li>Expand parent SKUs into their child components (depth ≤ 6)</li>
                  <li>Track per-node quantities, costs, and lead times</li>
                  <li>Roll up to a per-bid cost vs. quotation margin</li>
                </ul>
                <p className="pt-2">
                  Today, line-level pricing lives inside the bid&apos;s BoQ
                  (Quotation tab when it ships). Use the BoQ tab to verify what the buyer asked
                  for; use this tab as a placeholder for the BoM workflow.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Wrench className="w-3.5 h-3.5" />
            Backed by ``BomNode`` (backend/app/models/boq.py:78). Endpoint
            <code className="px-1 mx-0.5 bg-slate-100 rounded">/api/v2/bids/{"{id}"}/bom</code>
            ships with the editor.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default BomPanel;
